import React from 'react';

import Button from '@johnlewispartnership/wtr-ingredients/ingredients/Button';
import { TextLink } from '@johnlewispartnership/wtr-ingredients/ingredients/TextLink';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';

import { InputText } from 'components/Forms/ReduxFormFields';
import { myWaitroseCardNumberValidator, alreadyLinked } from 'components/MyWaitrose/Form/validator';

import { ApiError } from 'constants/errors';

import {
  WaitroseMultiLOBPermissions,
  usePermissions,
  ConsentEntity,
} from '@JohnLewisPartnership/permissions-universal-capture-component/waitrose';
import { useWtrSelector } from 'redux/hooks';
import { getAccessToken } from 'redux/modules/sessions/selectors';
import useIsClient from 'hooks/use-is-client';

import urls from 'constants/urls';

import styles from './Form.scss';

export interface FormProps {
  linkCard?: boolean;
  serverError?: ApiError | null;
  onSubmitErrorScroll?: (event: React.FormEvent<HTMLFormElement>) => void;
  onResetValidation?: () => void;
  submitted: boolean;
  submitting: boolean;
  children: React.ReactNode[] | React.ReactNode;
}

const Form = ({
  children,
  linkCard,
  serverError,
  onSubmitErrorScroll,
  onResetValidation,
  submitted,
  submitting,
}: FormProps) => {
  const isClient = useIsClient();
  const cardNumberValidator =
    serverError?.status === 409 ? alreadyLinked : myWaitroseCardNumberValidator;

  const jwt = useWtrSelector(getAccessToken);

  const { permissionsProps, controls } = usePermissions({
    mergeOptions: [
      {
        parentEntity: ConsentEntity.WTR,
        childEntities: [ConsentEntity.MYWTR],
      },
    ],
    leadText:
      'Would you like to receive exclusive deals, inspirational content, news and special offers from the John Lewis Partnership and our family of businesses, including John Lewis, Waitrose and John Lewis Money?',
    authToken: jwt?.replace('Bearer ', ''),
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    controls
      .submit()
      .catch(() => undefined) // Purposely ignore errors here as we want to allow the form to submit regardless of the outcome of the permissions submission
      .finally(() => onSubmitErrorScroll?.(event));
  };

  return (
    <form
      method="POST"
      onSubmit={handleSubmit}
      data-testid="my-waitrose-form"
      id="my-waitrose-form"
    >
      {linkCard && (
        <InputText
          name="myWaitroseCardNumber"
          id="myWaitroseCardNumber"
          label="myWaitrose card number"
          required
          onChange={onResetValidation}
          validate={cardNumberValidator}
        />
      )}
      {children}

      <hr />
      {isClient && <WaitroseMultiLOBPermissions {...permissionsProps} />}

      <div className={styles.marginTop}>
        <Typography styleAs="paragraph" textStyle="body-m">
          By creating an account you agree to the Waitrose & Partners{' '}
          <TextLink
            href={urls.termsAndConditions}
            target="_blank"
            underline="always"
            data-testid="terms-and-conditions-link"
          >
            <strong>terms and conditions</strong>
          </TextLink>{' '}
          and our{' '}
          <TextLink
            href={urls.privacyNotice}
            target="_blank"
            underline="always"
            data-testid="privacy-notice-link"
          >
            <strong>privacy notice</strong>
          </TextLink>
          .
        </Typography>
      </div>
      <div className={styles.marginTop}>
        <Button
          disabled={submitted}
          label={linkCard ? 'Link card' : 'Join myWaitrose'}
          type="submit"
          theme="finalising"
          waiting={submitting}
          width="fit"
          data-testid="form-submit-button"
        />
      </div>
    </form>
  );
};

export default Form;
